import {
  CalendarDays,
  ClipboardList,
  Download,
  Eye,
  FileSpreadsheet,
  FileText,
  Target,
  Trash2,
  UserRound,
  Users,
  X
} from "lucide-react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useLocale } from "./i18n";
import type { AppUser, Game, Goals, Metric, Opponent, Player, Serve } from "./types";
import {
  REPORT_TEMPLATES,
  type ReportFormat,
  type ReportScope,
  type ReportTemplate,
  type SavedReport,
  blobToPayload,
  downloadBlob,
  generateReport,
  loadReportHistory,
  payloadToBlob,
  pushReportHistory,
  removeReportHistory,
  scopeLabel,
  templateLabel
} from "./utils/reports";

const templateIcons: Record<ReportTemplate, ReactNode> = {
  summary: <FileText />,
  match: <Target />,
  player: <UserRound />,
  season: <FileSpreadsheet />
};

export function ReportsView({
  dateRange,
  games,
  goals,
  metric,
  onDateRangeChange,
  opponents,
  players,
  serves,
  user
}: {
  dateRange: { start?: string; end?: string };
  games: Game[];
  goals: Goals;
  metric: Metric;
  onDateRangeChange: (range: { start?: string; end?: string }) => void;
  opponents: Opponent[];
  players: Player[];
  serves: Serve[];
  user: AppUser;
}) {
  const { t, dateLocale } = useLocale();
  const [format, setFormat] = useState<ReportFormat>("pdf");
  const [scope, setScope] = useState<ReportScope>("all");
  const [template, setTemplate] = useState<ReportTemplate>("summary");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState<SavedReport[]>(() => loadReportHistory(user.uid));
  const [preview, setPreview] = useState<SavedReport | null>(null);

  useEffect(() => {
    setHistory(loadReportHistory(user.uid));
  }, [user.uid]);

  const previewUrl = useMemo(() => {
    if (!preview || preview.format !== "pdf") return null;
    return URL.createObjectURL(payloadToBlob(preview.payload, "pdf", preview.mimeType));
  }, [preview]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  async function runGenerate() {
    setBusy(true);
    setError("");
    try {
      const generated = await generateReport({
        format,
        scope,
        template,
        dateRange,
        metric,
        goals,
        players,
        serves,
        games,
        opponents
      });
      downloadBlob(generated.filename, generated.blob);
      const payload = generated.text ?? await blobToPayload(generated.blob, format);
      const saved: SavedReport = {
        id: crypto.randomUUID(),
        name: generated.title,
        createdAt: new Date().toISOString(),
        status: "ready",
        format,
        scope,
        template,
        dateRange: { ...dateRange },
        metric,
        filename: generated.filename,
        mimeType: generated.mimeType,
        payload
      };
      setHistory(pushReportHistory(user.uid, saved));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Could not generate report."));
    } finally {
      setBusy(false);
    }
  }

  function applyTemplate(next: ReportTemplate) {
    const preset = REPORT_TEMPLATES.find((entry) => entry.id === next);
    setTemplate(next);
    if (preset) setScope(preset.defaultScope);
  }

  function downloadSaved(report: SavedReport) {
    downloadBlob(report.filename, payloadToBlob(report.payload, report.format, report.mimeType));
  }

  function deleteSaved(id: string) {
    setHistory(removeReportHistory(user.uid, id));
    if (preview?.id === id) setPreview(null);
  }

  function localizedPeriod(range: { start?: string; end?: string }) {
    if (range.start || range.end) {
      return `${range.start || t("First serve")} – ${range.end || t("Latest")}`;
    }
    return t("All time");
  }

  return (
    <div className="reports-workspace">
      <section className="section-band">
        <div className="section-heading row-heading">
          <div>
            <p className="eyebrow">{t("Exports")}</p>
            <h2>{t("Reports")}</h2>
          </div>
          <button className="primary-button" onClick={runGenerate} disabled={busy}>
            <ClipboardList size={16} /> {busy ? t("Generating...") : t("Generate")}
          </button>
        </div>

        <div className="report-controls">
          <div className="field">
            <FileText size={16} />
            <select value={format} onChange={(event) => setFormat(event.target.value as ReportFormat)}>
              <option value="csv">{t("CSV export")}</option>
              <option value="html">{t("HTML report")}</option>
              <option value="pdf">{t("PDF report")}</option>
            </select>
          </div>
          <div className="field">
            <Users size={16} />
            <select value={scope} onChange={(event) => setScope(event.target.value as ReportScope)}>
              <option value="all">{t("All players")}</option>
              <option value="top">{t("Top servers")}</option>
              <option value="risk">{t("Risk review")}</option>
            </select>
          </div>
          <div className="field">
            <FileSpreadsheet size={16} />
            <select value={template} onChange={(event) => setTemplate(event.target.value as ReportTemplate)}>
              <option value="summary">{t("Summary")}</option>
              <option value="match">{t("Match prep")}</option>
              <option value="player">{t("Player review")}</option>
              <option value="season">{t("Season packet")}</option>
            </select>
          </div>
          <div className="field">
            <CalendarDays size={16} />
            <input
              type="date"
              aria-label={t("Report start date")}
              value={dateRange.start ?? ""}
              onChange={(event) => onDateRangeChange({ ...dateRange, start: event.target.value || undefined })}
            />
          </div>
          <div className="field">
            <CalendarDays size={16} />
            <input
              type="date"
              aria-label={t("Report end date")}
              value={dateRange.end ?? ""}
              onChange={(event) => onDateRangeChange({ ...dateRange, end: event.target.value || undefined })}
            />
          </div>
          {(dateRange.start || dateRange.end) && (
            <button className="text-button" onClick={() => onDateRangeChange({})}>
              <X size={14} /> {t("Clear dates")}
            </button>
          )}
          <span className="section-chip">{localizedPeriod(dateRange)} · {metric}</span>
          {user.isDemo && <span className="section-chip">{t("Local demo export")}</span>}
        </div>
        {error && <p className="form-error">{error}</p>}

        <div className="report-list">
          {history.length === 0 ? (
            <p className="empty-hint">{t("Generated reports stay on this device. Use Generate or a template to create one.")}</p>
          ) : (
            history.map((report) => (
              <article key={report.id} className="report-row report-row-history">
                <FileText />
                <div>
                  <strong>{report.name}</strong>
                  <span>
                    {new Intl.DateTimeFormat(dateLocale, { dateStyle: "medium", timeStyle: "short" }).format(new Date(report.createdAt))}
                    {" · "}
                    {scopeLabel(report.scope)}
                    {" · "}
                    {report.format.toUpperCase()}
                    {" · "}
                    {localizedPeriod(report.dateRange)}
                  </span>
                </div>
                <span className="status-pill ready">{t("Ready")}</span>
                <button className="icon-button" aria-label={t("Preview report")} onClick={() => setPreview(report)}>
                  <Eye size={18} />
                </button>
                <button className="icon-button" aria-label={t("Download report")} onClick={() => downloadSaved(report)}>
                  <Download size={18} />
                </button>
                <button className="icon-button" aria-label={t("Delete report")} onClick={() => deleteSaved(report.id)}>
                  <Trash2 size={18} />
                </button>
              </article>
            ))
          )}
        </div>
      </section>

      <section className="template-grid">
        {REPORT_TEMPLATES.map((entry) => (
          <article key={entry.id} className={`template-card ${template === entry.id ? "active" : ""}`}>
            {templateIcons[entry.id]}
            <div>
              <h2>{t(entry.name)}</h2>
              <p>{t(entry.detail)}</p>
            </div>
            <button className="ghost-button" onClick={() => applyTemplate(entry.id)}>
              {template === entry.id ? t("Selected") : t("Use template")}
            </button>
          </article>
        ))}
      </section>

      {preview && (
        <div className="report-preview-shell" role="dialog" aria-modal="true" aria-label={t("Report preview")}>
          <div className="report-preview-card">
            <header>
              <div>
                <p className="eyebrow">{templateLabel(preview.template)}</p>
                <h2>{preview.name}</h2>
              </div>
              <div className="report-preview-actions">
                <button className="ghost-button" onClick={() => downloadSaved(preview)}>
                  <Download size={16} /> {t("Download")}
                </button>
                <button className="icon-button" aria-label={t("Close preview")} onClick={() => setPreview(null)}>
                  <X size={18} />
                </button>
              </div>
            </header>
            {preview.format === "html" && (
              <iframe className="report-preview-frame" title={t("HTML report preview")} srcDoc={preview.payload} />
            )}
            {preview.format === "csv" && <pre className="report-preview-csv">{preview.payload}</pre>}
            {preview.format === "pdf" && previewUrl && (
              <iframe className="report-preview-frame" title={t("PDF report preview")} src={previewUrl} />
            )}
          </div>
          <button className="report-preview-scrim" aria-label={t("Close preview overlay")} onClick={() => setPreview(null)} />
        </div>
      )}
    </div>
  );
}
